import React, { useEffect, useState } from 'react';
import {
  Box,
  Checkbox,
  FormControl,
  FormControlLabel,
  FormGroup,
  FormHelperText,
  Radio,
  TextField,
  Typography,
} from '@mui/material';
import { FormattedMessage } from 'react-intl';
import {
  PartialBy,
  QuestionTypeV2,
  QuestionChoiceWithText,
  DigitalSubmissionChoiceWithText,
} from '../../../redux/types';
import useDebounce from '../../../hooks/useDebounce';

export type Props = {
  question: QuestionChoiceWithText;
  sectionId?: number;
  enrollmentId?: number;
  caseId?: number;
  disabled?: boolean;
  onSave?: (vals?: PartialBy<DigitalSubmissionChoiceWithText<QuestionChoiceWithText['type']>, 'id'>) => Promise<void>;
  defaultSubmission?: DigitalSubmissionChoiceWithText<QuestionChoiceWithText['type']>;
  expand?: boolean;
  summarizedView?: boolean;
  deprecated?: boolean;
}

const ChoiceWithTexQuestionInput: React.FC<Props> = ({
  question,
  sectionId,
  enrollmentId,
  caseId,
  disabled,
  defaultSubmission,
  expand,
  summarizedView: summarized,
  onSave,
  deprecated,
}: Props) => {
  const [saved, setSaved] = useState<boolean>(false);
  const [error, setError] = useState<string>();
  const [answer, setAnswer] = useState<DigitalSubmissionChoiceWithText<QuestionChoiceWithText['type']>['answer']>(defaultSubmission?.answer || {});

  useEffect(() => {
    setSaved(false);
    setError(undefined);
  }, [caseId, enrollmentId, sectionId]);

  const getDigitalSubmissionObj = (selectedAnswer: DigitalSubmissionChoiceWithText<QuestionChoiceWithText['type']>['answer']): PartialBy<DigitalSubmissionChoiceWithText<QuestionChoiceWithText['type']>, 'id'> | undefined => {
    if (sectionId === undefined || enrollmentId === undefined) {
      return undefined;
    }
    if (defaultSubmission) {
      return {
        ...defaultSubmission,
        answer: selectedAnswer,
      };
    }
    return {
      answer: selectedAnswer,
      digitalQuestionId: question.id,
      digitalSectionId: sectionId,
      enrollmentId,
      digitalCaseId: caseId,
      type: QuestionTypeV2.ChoiceWithText,
    };
  };

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>, checked: boolean) => {
    const optionId = Number(event.target.name);
    setSaved(false);
    let payload: DigitalSubmissionChoiceWithText<QuestionChoiceWithText['type']>['answer'] = {
      ...defaultSubmission?.answer,
      [optionId]: {
        selected: checked,
        text: null,
      },
    };
    if (question.payload?.multi === false) {
      payload = {
        [optionId]: {
          selected: checked,
          text: null,
        },
      };
    }
    const submissionObj = getDigitalSubmissionObj(payload);
    if (onSave && submissionObj) {
      onSave(submissionObj)
        .then(() => {
          setSaved(true);
        })
        .catch(() => {
          setError('Unable to save, please check your internet connection');
        }).finally(() => {
          setAnswer(defaultSubmission?.answer || {});
        });
    }
  };

  const debounceCallback = (debouncedAnswer: DigitalSubmissionChoiceWithText<QuestionChoiceWithText['type']>['answer']) => {
    if (onSave) {
      onSave(getDigitalSubmissionObj(debouncedAnswer))
        .then(() => {
          setSaved(true);
        })
        .catch(() => {
          setError('Unable to save, please check your internet connection');
        });
    }
  };

  const debounceAnswer = useDebounce<DigitalSubmissionChoiceWithText<QuestionChoiceWithText['type']>['answer']>(debounceCallback, 1000);

  const onTextChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSaved(false);
    setError(undefined);
    const { value } = e.target;
    const optId = Number(e.target.name);
    const submissionAnsObj = defaultSubmission?.answer;

    const ansPayload: DigitalSubmissionChoiceWithText<QuestionChoiceWithText['type']>['answer'] = {
      ...submissionAnsObj,
      [optId]: {
        selected: submissionAnsObj?.[optId].selected || true,
        text: value,
      },
    };

    setAnswer(ansPayload);
    debounceAnswer(ansPayload);
  };

  const qPayloadOptions = summarized
    ? question.payload?.options
      .filter((po) => !!defaultSubmission?.answer[po.id]?.selected)
    : question.payload?.options;

  if (deprecated) {
    return (
      <Box>
        <Typography sx={{ textDecorationLine: 'line-through' }} color="grey.400">
          {question.text}
        </Typography>
        <Typography variant="caption">Deprecated</Typography>
      </Box>
    );
  }

  return (
    <Box>
      <FormControl fullWidth error={!!error} component="fieldset">
        <Typography fontWeight={500} gutterBottom>{question.text}</Typography>
        <FormGroup>
          {
            qPayloadOptions && qPayloadOptions.map((p) => (
              <React.Fragment key={p.id}>
                <FormControlLabel
                  key={p.id}
                  control={question.payload?.multi ? (
                    <Checkbox
                      checked={defaultSubmission?.answer[p.id]?.selected || false}
                      onChange={handleChange}
                      name={p.id.toString()}
                    />
                  ) : (
                    <Radio
                      checked={defaultSubmission?.answer[p.id]?.selected || false}
                      onChange={handleChange}
                      name={p.id.toString()}
                    />
                  )}
                  label={p.text}
                  disabled={disabled}
                />
                {
                  (defaultSubmission?.answer[p.id]?.selected || expand)
                  && p.textBox
                  && (
                    <TextField
                      autoComplete="off"
                      placeholder={p.placeholder || ''}
                      value={answer[p.id]?.text || ''}
                      onChange={onTextChange}
                      name={p.id.toString()}
                      disabled={disabled}
                      size="small"
                    />
                  )
                }
              </React.Fragment>
            ))
          }
        </FormGroup>
        {
          !!error && (
            <FormHelperText>{error}</FormHelperText>
          )
        }
        {
          saved && (
            <FormHelperText>
              <FormattedMessage id="submission.saved" />
            </FormHelperText>
          )
        }
      </FormControl>
    </Box>
  );
};

export default ChoiceWithTexQuestionInput;
